/**
 * TreatX Clinic Representative Authentication Context
 * Handles authentication for clinic representatives (post-sale operations)
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

const ClinicRepresentativeAuthContext = createContext();

export const useClinicRepAuth = () => {
  const context = useContext(ClinicRepresentativeAuthContext);
  if (!context) {
    throw new Error('useClinicRepAuth must be used within ClinicRepresentativeAuthProvider');
  }
  return context;
};

// P0-INTEGRITY: Representative accounts removed — unified auth is the only login path
// The route /clinic/representative/login already redirects to /login
// Representative authentication is handled through the unified RBAC system
const representativeAccounts = [];

// Storage key for persistence
const STORAGE_KEY = 'treatx_clinic_rep';

// Initialize from localStorage
const getInitialRepresentative = () => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading clinic representative:', e);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }
  return null;
};

export const ClinicRepresentativeAuthProvider = ({ children }) => {
  const [representative, setRepresentative] = useState(getInitialRepresentative);
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getInitialRepresentative());
  const [loading, setLoading] = useState(false);

  const loginRepresentative = useCallback(async (email, password) => {
    setLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const account = representativeAccounts.find(
      acc => acc.email.toLowerCase() === email.toLowerCase() && acc.password === password
    );
    
    setLoading(false);
    
    if (account) {
      const { password: _, ...safeAccount } = account;
      setRepresentative(safeAccount);
      setIsAuthenticated(true);
      // Persist to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(safeAccount));
      return { success: true, representative: safeAccount };
    }
    
    return { success: false, error: 'Invalid email or password' };
  }, []);

  const logoutRepresentative = useCallback(() => {
    setRepresentative(null);
    setIsAuthenticated(false);
    // Clear from localStorage
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value = {
    representative,
    isAuthenticated,
    loading,
    loginRepresentative,
    logoutRepresentative,
    representativeAccounts: representativeAccounts.map(({ password, ...rest }) => rest)
  };

  return (
    <ClinicRepresentativeAuthContext.Provider value={value}>
      {children}
    </ClinicRepresentativeAuthContext.Provider>
  );
};

export default ClinicRepresentativeAuthContext;
